import { useEffect, useId, useRef, useState } from 'react';

/**
 * The ⓘ button used beside every feature name on the NGO OS pricing page.
 *
 * Desktop: hover opens after 200ms, click pins it open until dismissed.
 * Mobile: tap toggles; hover is never the only way in.
 * Escape closes, and the panel flips below the trigger when it would other-
 * wise run off the top of the viewport.
 */
export default function InfoTip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [below, setBelow] = useState(false);
  const timer = useRef<number | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const id = useId();

  const show = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const r = wrap.current?.getBoundingClientRect();
      // 160px is a safe worst-case panel height; flip rather than clip.
      setBelow(!!r && r.top < 160);
      setOpen(true);
    }, 200);
  };
  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    if (!pinned) setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setPinned(false); }
    };
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) {
        setOpen(false); setPinned(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <span ref={wrap} className="ngoos-tip-wrap">
      <button
        type="button"
        className="ngoos-tip-btn"
        aria-label="More information"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={() => setOpen(true)}
        onBlur={() => !pinned && setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          const next = !pinned;
          setPinned(next);
          setOpen(next || true);
          if (!next) setOpen(false);
        }}
      >
        i
      </button>
      {open && (
        <span role="tooltip" id={id} className={`ngoos-tip ${below ? 'below' : 'above'}`}>
          {text}
        </span>
      )}
    </span>
  );
}
